import type { IStructureStore, Component, AppStructure } from '@wix/thunderbolt-symbols'
import { COMP_PATH_DELIMITER } from '@wix/thunderbolt-becky-types'

const traversePageComponents = (
	structure: AppStructure,
	recursiveFunction: (compId: string, component: Component) => void
): void => {
	Object.entries(structure)
		.filter(([_, component]) => component?.componentType === 'Page')
		.forEach(([compId, component]) => recursiveFunction(compId, component))
}

export const getCompIdToTemplateCompPath = (structureStore: IStructureStore): Record<string, string> => {
	const structure = structureStore.getEntireStore()
	const compIdToTemplateCompPath: Record<string, string> = {}

	// visit all components recursively based on the structure and add their template comp path in the compIdToTemplateCompPath map
	// the template comp path looks like: comp-1/comp-2/comp-3
	// when we encounter a repeater we add [] to the path like so: comp-1/repeater-1/[]/comp-inside-repeater-item
	// in the *resolved* comp path inside the [] there will be the item id of the repeater item, so if a repeater has two items: item-1, item-2
	// the resolved comp paths will be: comp-1/repeater-1/[item-1]/comp-inside-repeater-item and comp-1/repeater-1/[item-2]/comp-inside-repeater-item
	const mapComponentToTemplatePath = (compId: string, component: Component, accumulatedPath: string = ''): void => {
		const fullPath = `${accumulatedPath}${compId}`
		compIdToTemplateCompPath[compId] = fullPath

		const childComponentIds = component?.components ?? []
		const childPathPrefix = `${fullPath}${COMP_PATH_DELIMITER}`

		childComponentIds.forEach((childId) => {
			const newChildPathPrefix =
				component?.componentType === 'Repeater' ? `${childPathPrefix}[]${COMP_PATH_DELIMITER}` : childPathPrefix
			mapComponentToTemplatePath(childId, structure[childId], newChildPathPrefix)
		})
	}

	traversePageComponents(structure, mapComponentToTemplatePath)

	return compIdToTemplateCompPath
}
